import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ImageViewer from '../components/ImageViewer';
import { useToast } from '../components/Toast';
import { colors } from '../theme/tokens';

const AddData = () => {
  const { token, canAddData, user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const [form, setForm] = useState({
    make: '',
    model: '',
    variant: '',
    from: '',
    to: '',
    parts: [{ position: '', code: '', quantity: '', unit: 'x' }],
  });
  const [loading, setLoading] = useState(false);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [existingParts, setExistingParts] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [selectedPartCode, setSelectedPartCode] = useState(null);
  const [isCustomMake, setIsCustomMake] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [customMake, setCustomMake] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [variants, setVariants] = useState([]);
  const [yearRanges, setYearRanges] = useState([]);
  const [yearRangesByVariant, setYearRangesByVariant] = useState({});
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [isCustomVariant, setIsCustomVariant] = useState(false);
  const [isCustomYearRange, setIsCustomYearRange] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!canAddData) {
      navigate('/');
    }
  }, [canAddData, navigate]);

  // Load makes on component mount
  useEffect(() => {
    fetchMakes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load models when make changes
  useEffect(() => {
    if (form.make) {
      fetchModels(form.make);
    } else {
      setModels([]);
      setForm(prev => ({ ...prev, model: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.make]);

  // Check for existing parts when all car fields are complete
  useEffect(() => {
    if (form.make && form.model && form.variant && form.from && form.to) {
      checkExistingParts();
    } else {
      setExistingParts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.make, form.model, form.variant, form.from, form.to]);

  const checkExistingParts = async () => {
    try {
      setLoadingExisting(true);
      const response = await axios.get(`${API_URL}/api/cars/check-existing`, {
        params: {
          make: form.make,
          model: form.model,
          variant: form.variant,
          from: form.from,
          to: form.to
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.exists) {
        setExistingParts(response.data.parts);
      } else {
        setExistingParts([]);
      }
    } catch (error) {
      console.error('Failed to check existing parts:', error);
      setExistingParts([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  const fetchMakes = async () => {
    try {
      setLoadingMakes(true);
      const response = await axios.get(`${API_URL}/api/car-list/makes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMakes(response.data);
    } catch (error) {
      console.error('Failed to fetch makes:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
      showToast('Failed to load car makes: ' + errorMsg, 'error');
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchModels = async (make) => {
    try {
      setLoadingModels(true);
      const response = await axios.get(`${API_URL}/api/car-list/models/${encodeURIComponent(make)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModels(response.data);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      showToast('Failed to load models for ' + make, 'error');
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchVariantsAndYears = async (make, model) => {
    try {
      setLoadingVariants(true);
      const response = await axios.get(
        `${API_URL}/api/car-list/variants/${encodeURIComponent(make)}/${encodeURIComponent(model)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVariants(response.data.variants || []);
      setYearRangesByVariant(response.data.yearRangesByVariant || {});
      setYearRanges([]);
    } catch (error) {
      setVariants([]);
      setYearRangesByVariant({});
      setYearRanges([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  const clearVariantState = () => {
    setVariants([]);
    setYearRanges([]);
    setYearRangesByVariant({});
    setIsCustomVariant(false);
    setIsCustomYearRange(false);
  };

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMakeChange = (e) => {
    const value = e.target.value;
    clearVariantState();
    if (value === '__REQUEST_NEW__') {
      setIsCustomMake(true);
      setForm({ ...form, make: '', model: '', variant: '', from: '', to: '' });
      setCustomMake('');
    } else {
      setIsCustomMake(false);
      setForm({ ...form, make: value, model: '', variant: '', from: '', to: '' });
    }
  };

  const handleCustomMakeChange = (e) => {
    const value = e.target.value;
    setCustomMake(value);
    setForm({ ...form, make: value });
  };

  const handleModelSelectChange = (e) => {
    const value = e.target.value;
    clearVariantState();
    if (value === '__REQUEST_NEW__') {
      setIsCustomModel(true);
      setForm({ ...form, model: '', variant: '', from: '', to: '' });
      setCustomModel('');
    } else {
      setIsCustomModel(false);
      setForm({ ...form, model: value, variant: '', from: '', to: '' });
      if (value && form.make && !isCustomMake) {
        fetchVariantsAndYears(form.make, value);
      }
    }
  };

  const handleCustomModelChange = (e) => {
    const value = e.target.value;
    setCustomModel(value);
    setForm({ ...form, model: value });
  };

  const handleVariantSelectChange = (e) => {
    const value = e.target.value;
    setIsCustomYearRange(false);
    if (value === '__NEW_VARIANT__') {
      setIsCustomVariant(true);
      setYearRanges([]);
      setForm(prev => ({ ...prev, variant: '', from: '', to: '' }));
    } else {
      setIsCustomVariant(false);
      setForm(prev => ({ ...prev, variant: value, from: '', to: '' }));
      if (value && yearRangesByVariant[value]) {
        setYearRanges(yearRangesByVariant[value]);
      } else {
        setYearRanges([]);
      }
    }
  };

  const handleYearRangeSelectChange = (e) => {
    const value = e.target.value;
    if (value === '__NEW_YEAR_RANGE__') {
      setIsCustomYearRange(true);
      setForm(prev => ({ ...prev, from: '', to: '' }));
    } else if (value) {
      const [from, to] = value.split('-');
      setIsCustomYearRange(false);
      setForm(prev => ({ ...prev, from, to }));
    } else {
      setForm(prev => ({ ...prev, from: '', to: '' }));
    }
  };

  const handlePartChange = (index, e) => {
    const updatedParts = [...form.parts];
    updatedParts[index][e.target.name] = e.target.value;
    setForm({ ...form, parts: updatedParts });
  };

  const addPartField = () => {
    setForm({ ...form, parts: [...form.parts, { position: '', code: '', quantity: '', unit: 'x' }] });
  };

  const removePartField = (index) => {
    const updatedParts = form.parts.filter((_, i) => i !== index);
    setForm({ ...form, parts: updatedParts });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.make || !form.model || !form.variant || !form.from || !form.to) {
      showToast('Please fill in all car details', 'warning');
      return;
    }

    const emptyParts = form.parts.some(p => !p.position || !p.code || !p.quantity);
    if (emptyParts) {
      showToast('Please fill in all part details', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/cars`, {
        make: form.make,
        model: form.model,
        variant: form.variant,
        yearRange: {
          from: Number(form.from),
          to: Number(form.to),
        },
        parts: form.parts.map(p => ({
          position: p.position,
          code: p.code,
          quantity: Number(p.quantity),
          unit: p.unit,
        })),
        isCustomMake: isCustomMake,
        isCustomModel: isCustomModel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      let message = response.data.message;
      if (isCustomMake || isCustomModel) {
        message += ' Your custom make/model will be added to the list once approved.';
      }
      showToast(message, 'success');
      
      setForm({
        make: '',
        model: '',
        variant: '',
        from: '',
        to: '',
        parts: [{ position: '', code: '', quantity: '', unit: 'x' }],
      });
      setIsCustomMake(false);
      setIsCustomModel(false);
      setCustomMake('');
      setCustomModel('');
      clearVariantState();
    } catch (error) {
      showToast('Error submitting data: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container} className="container-mobile-padding">
      <h2 style={styles.title}>➕ Add Car Parts</h2>
      
      <div style={styles.infoCard} className="card-mobile-padding">
        <strong>🥇 Gold Feature</strong>
        <p>Submit new car parts to our database. All submissions require Platinum admin approval.</p>
        <p>✨ Earn 1 token for each approved submission!</p>
      </div>

      <form onSubmit={handleSubmit} style={styles.form} className="card-mobile-padding">
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Car Information</h3>
          <div style={styles.grid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Make *</label>
              {loadingMakes ? (
                <div style={styles.loading}>Loading makes...</div>
              ) : (
                <>
                  {!isCustomMake ? (
                    <select 
                      name="make" 
                      value={form.make} 
                      onChange={handleMakeChange}
                      style={styles.select}
                      required
                    >
                      <option value="">-- Select Make --</option>
                      {makes.map(make => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                      <option value="__REQUEST_NEW__" style={{fontWeight: 'bold', color: '#2fb65c'}}>
                        + Request New Make
                      </option>
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        placeholder="Enter new make name (e.g., Tesla, BYD)"
                        value={customMake}
                        onChange={handleCustomMakeChange}
                        style={{...styles.input, borderColor: '#2fb65c', borderWidth: '2px'}}
                        required
                      />
                      <small style={styles.customHint}>
                        💡 New make - will be added to list after admin approval
                        <button
                          type="button"
                          onClick={() => {
                            setIsCustomMake(false);
                            setCustomMake('');
                            setForm({...form, make: ''});
                          }}
                          style={styles.cancelCustomBtn}
                        >
                          Cancel
                        </button>
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Model *</label>
              {loadingModels ? (
                <div style={styles.loading}>Loading models...</div>
              ) : (
                <>
                  {!isCustomModel ? (
                    <select 
                      name="model" 
                      value={form.model} 
                      onChange={handleModelSelectChange}
                      style={styles.select}
                      required
                      disabled={!form.make}
                    >
                      <option value="">-- Select Model --</option>
                      {models.map(model => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                      {form.make && (
                        <option value="__REQUEST_NEW__" style={{fontWeight: 'bold', color: '#2fb65c'}}>
                          + Request New Model
                        </option>
                      )}
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        placeholder="Enter new model name (e.g., Model S, Camry)"
                        value={customModel}
                        onChange={handleCustomModelChange}
                        style={{...styles.input, borderColor: '#2fb65c', borderWidth: '2px'}}
                        required
                      />
                      <small style={styles.customHint}>
                        💡 New model for {form.make} - will be added to list after admin approval
                        <button
                          type="button"
                          onClick={() => {
                            setIsCustomModel(false);
                            setCustomModel('');
                            setForm({...form, model: ''});
                          }}
                          style={styles.cancelCustomBtn}
                        >
                          Cancel
                        </button>
                      </small>
                    </div>
                  )}
                </>
              )}
              {!form.make && (
                <small style={styles.hint}>Select a make first</small>
              )}
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Variant *</label>
              {loadingVariants ? (
                <div style={styles.loading}>Loading variants...</div>
              ) : variants.length > 0 && !isCustomMake && !isCustomModel ? (
                !isCustomVariant ? (
                  <select
                    value={form.variant}
                    onChange={handleVariantSelectChange}
                    style={styles.select}
                    required
                  >
                    <option value="">-- Select Variant --</option>
                    {variants.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                    <option value="__NEW_VARIANT__" style={{ fontWeight: 'bold', color: '#2fb65c' }}>
                      + New Variant
                    </option>
                  </select>
                ) : (
                  <div>
                    <input
                      type="text"
                      name="variant"
                      placeholder="Enter new variant (e.g., XLE, TDI)"
                      value={form.variant}
                      onChange={handleInputChange}
                      style={{ ...styles.input, borderColor: '#2fb65c', borderWidth: '2px' }}
                      required
                    />
                    <small style={styles.customHint}>
                      New variant
                      <button
                        type="button"
                        onClick={() => {
                          setIsCustomVariant(false);
                          setForm(prev => ({ ...prev, variant: '', from: '', to: '' }));
                          setYearRanges([]);
                        }}
                        style={styles.cancelCustomBtn}
                      >
                        Cancel
                      </button>
                    </small>
                  </div>
                )
              ) : (
                <input
                  name="variant"
                  placeholder="XLE"
                  value={form.variant}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              )}
            </div>
          </div>
          {yearRanges.length > 0 && !isCustomVariant && !isCustomYearRange ? (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Year Range *</label>
              <select
                value={form.from && form.to ? `${form.from}-${form.to}` : ''}
                onChange={handleYearRangeSelectChange}
                style={styles.select}
                required
              >
                <option value="">-- Select Year Range --</option>
                {yearRanges.map(yr => (
                  <option key={yr.display} value={`${yr.from}-${yr.to}`}>{yr.display}</option>
                ))}
                <option value="__NEW_YEAR_RANGE__" style={{ fontWeight: 'bold', color: '#2fb65c' }}>
                  + New Year Range
                </option>
              </select>
            </div>
          ) : (
            <div style={styles.row} className="grid-2col">
              <div style={styles.inputGroup}>
                <label style={styles.label}>Year From *</label>
                <input
                  name="from"
                  placeholder="2018"
                  type="number"
                  value={form.from}
                  onChange={handleInputChange}
                  style={isCustomYearRange ? { ...styles.input, borderColor: '#2fb65c', borderWidth: '2px' } : styles.input}
                  required
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Year To *</label>
                <input
                  name="to"
                  placeholder="2022"
                  type="number"
                  value={form.to}
                  onChange={handleInputChange}
                  style={isCustomYearRange ? { ...styles.input, borderColor: '#2fb65c', borderWidth: '2px' } : styles.input}
                  required
                />
              </div>
              {isCustomYearRange && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <small style={styles.customHint}>
                    New year range
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomYearRange(false);
                        setForm(prev => ({ ...prev, from: '', to: '' }));
                      }}
                      style={styles.cancelCustomBtn}
                    >
                      Cancel
                    </button>
                  </small>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Existing Parts Preview */}
        {form.make && form.model && form.variant && form.from && form.to && (
          <div style={styles.existingSection}>
            <h3 style={styles.existingSectionTitle}>
              📋 Existing Parts for this Car
              {loadingExisting && <span style={styles.loadingText}> (Loading...)</span>}
            </h3>
            {!loadingExisting && existingParts.length === 0 && (
              <p style={styles.noParts}>✨ No parts added yet - you'll be the first!</p>
            )}
            {!loadingExisting && existingParts.length > 0 && (
              <>
                <p style={styles.existingInfo}>
                  ⚠️ These parts are already in the system. Please avoid adding duplicates.
                </p>
                <div style={styles.existingPartsList}>
                  {existingParts.map((part, i) => (
                    <div key={part._id || i} style={styles.existingPartItem}>
                      <div style={styles.existingPartInfo} className="grid-parts-result">
                        <span style={styles.existingPartPosition}>{part.position}</span>
                        <span 
                          style={styles.existingPartCode}
                          onClick={() => setSelectedPartCode(part.code)}
                          title="Click to view product image"
                        >
                          {part.code}
                        </span>
                        <span style={styles.existingPartQuantity}>
                          {part.quantity}{part.unit}
                        </span>
                        <span style={{
                          ...styles.statusBadge,
                          ...(part.status === 'approved' ? styles.approvedBadge : styles.pendingBadge)
                        }}>
                          {part.status === 'approved' ? '✓ Approved' : '⏳ Pending'}
                        </span>
                      </div>
                      {/* Show user attribution for Gold and Platinum users */}
                      {(user.tier === 'gold' || user.tier === 'platinum') && part.addedBy && (
                        <div style={styles.userAttribution}>
                          <span 
                            style={styles.userIcon}
                            onMouseEnter={() => setHoveredPart(i)}
                            onMouseLeave={() => setHoveredPart(null)}
                          >
                            👤
                            <span style={{
                              ...styles.tooltip,
                              visibility: hoveredPart === i ? 'visible' : 'hidden',
                              opacity: hoveredPart === i ? 1 : 0
                            }}>
                              <strong>{part.addedBy.name}</strong>
                              <br />
                              {new Date(part.createdAt).toLocaleDateString()}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Add New Parts</h3>
          {form.parts.map((part, index) => (
            <div key={index} style={styles.partRow}>
              <div style={styles.partGrid} className="grid-4col-parts">
                <input 
                  name="position" 
                  placeholder="Position (e.g., Front Bumper)" 
                  value={part.position} 
                  onChange={(e) => handlePartChange(index, e)}
                  style={styles.input}
                  required
                />
                <input 
                  name="code" 
                  placeholder="Part Code" 
                  value={part.code} 
                  onChange={(e) => handlePartChange(index, e)}
                  style={styles.input}
                  required
                />
                <input 
                  name="quantity" 
                  placeholder="Qty" 
                  type="number"
                  value={part.quantity} 
                  onChange={(e) => handlePartChange(index, e)}
                  style={styles.input}
                  required
                />
                <select 
                  name="unit" 
                  value={part.unit} 
                  onChange={(e) => handlePartChange(index, e)}
                  style={styles.input}
                >
                  <option value="x">pcs (x)</option>
                  <option value="m">meters (m)</option>
                </select>
              </div>
              {form.parts.length > 1 && (
                <button 
                  type="button" 
                  onClick={() => removePartField(index)}
                  style={styles.removeBtn}
                >
                  ✗
                </button>
              )}
            </div>
          ))}
          <button 
            type="button" 
            onClick={addPartField}
            style={styles.addPartBtn}
          >
            + Add Another Part
          </button>
        </div>

        <button 
          type="submit" 
          style={styles.submitBtn}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </form>

      {/* Image Viewer Modal */}
      {selectedPartCode && (
        <ImageViewer 
          partCode={selectedPartCode} 
          onClose={() => setSelectedPartCode(null)} 
        />
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '900px', margin: '20px auto', padding: '20px' },
  title: { textAlign: 'center', color: colors.text, marginBottom: '20px' },
  infoCard: { background: colors.greenSoft, padding: '20px', borderRadius: '10px', marginBottom: '25px', borderLeft: '4px solid #91C83E', color: colors.text },
  form: { background: colors.surface, padding: '30px', borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  section: { marginBottom: '30px' },
  sectionTitle: { marginBottom: '15px', color: '#2fb65c', borderBottom: '2px solid #2fb65c', paddingBottom: '10px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
  inputGroup: { display: 'flex', flexDirection: 'column' },
  label: { marginBottom: '5px', fontWeight: '600', color: colors.textMuted, fontSize: '14px' },
  input: { padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '16px', width: '100%', boxSizing: 'border-box', background: colors.inputBg, color: colors.text },
  select: { padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '16px', background: colors.inputBg, color: colors.text, cursor: 'pointer', width: '100%', boxSizing: 'border-box' },
  hint: { fontSize: '12px', color: colors.textFaint, marginTop: '5px' },
  loading: { padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', background: colors.surfaceSoft, color: colors.textMuted, textAlign: 'center' },
  partRow: { display: 'flex', gap: '10px', marginBottom: '15px', alignItems: 'flex-start' },
  partGrid: { flex: 1, display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: '10px' },
  removeBtn: { padding: '12px 15px', background: colors.red, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' },
  addPartBtn: { padding: '10px 20px', background: '#2fb65c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitBtn: { width: '100%', padding: '15px', background: '#2fb65c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '18px', fontWeight: '600', cursor: 'pointer', marginTop: '20px' },
  existingSection: { 
    background: colors.greenSoft, 
    padding: '20px', 
    borderRadius: '10px', 
    marginBottom: '30px', 
    border: '2px solid #91C83E',
    boxShadow: '0 2px 8px rgba(145, 200, 62, 0.2)'
  },
  existingSectionTitle: { 
    marginBottom: '15px', 
    color: '#2fb65c', 
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  loadingText: { fontSize: '14px', color: colors.textFaint, fontStyle: 'italic' },
  noParts: { 
    textAlign: 'center', 
    padding: '20px', 
    color: '#2fb65c', 
    fontSize: '16px',
    fontWeight: '500'
  },
  existingInfo: { 
    background: colors.amberSoft, 
    padding: '10px 15px', 
    borderRadius: '6px', 
    marginBottom: '15px',
    color: colors.text,
    fontSize: '14px',
    border: '1px solid #91C83E'
  },
  existingPartsList: { 
    display: 'flex', 
    flexDirection: 'column', 
    gap: '10px' 
  },
  existingPartItem: { 
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 15px', 
    background: colors.surface, 
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  existingPartInfo: {
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 1fr auto',
    gap: '15px',
    alignItems: 'center',
    flex: 1
  },
  existingPartPosition: { fontWeight: '600', color: colors.text },
  existingPartCode: { 
    color: '#2fb65c', 
    fontFamily: 'monospace', 
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: '600',
    transition: 'color 0.2s'
  },
  existingPartQuantity: { color: colors.textMuted, fontSize: '14px' },
  statusBadge: { 
    padding: '4px 12px', 
    borderRadius: '12px', 
    fontSize: '12px', 
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },
  approvedBadge: { background: colors.greenSoft, color: colors.text },
  pendingBadge: { background: colors.amberSoft, color: colors.text },
  userAttribution: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    marginLeft: '10px'
  },
  userIcon: { 
    position: 'relative',
    cursor: 'pointer', 
    fontSize: '18px',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '50%',
    transition: 'background 0.2s'
  },
  tooltip: {
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: '8px',
    background: '#333',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    zIndex: 1000,
    transition: 'opacity 0.2s',
    pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
  },
  customHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#2fb65c',
    marginTop: '8px',
    padding: '8px',
    background: colors.greenSoft,
    borderRadius: '4px',
    fontWeight: '500'
  },
  cancelCustomBtn: {
    marginLeft: '10px',
    padding: '4px 12px',
    background: colors.surface2,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
    color: colors.text
  }
};

export default AddData;
